import React, { useState, useEffect } from 'react';
import { api } from '../api';

interface LoginPageProps {
  onLogin: (user: { email: string }) => void;
  loading?: boolean;
}

export const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msBusy, setMsBusy] = useState(false);

  // Check if user is already logged in
  const checkAuth = async () => {
    try {
      console.log('checkAuth: Fetching /api/user...');
      // Check auth on same domain where cookie is set
      const res = await fetch('/api/user', { 
        credentials: 'include'
      });
      console.log('checkAuth: Response status:', res.status);
      if (res.ok) {
        const raw = await res.json();
        console.log('checkAuth: User data:', raw);
        const container = raw?.user && typeof raw.user === 'object' ? raw.user : raw;
        const userEmail = container?.email || (Array.isArray(container?.emails) ? container.emails[0] : undefined);
        console.log('checkAuth: Extracted email:', userEmail);
        if (userEmail) {
          // Warm up subcontractor API session
          try { await api.health(); } catch { /* non-fatal */ }
          console.log('checkAuth: Calling onLogin with email:', userEmail);
          onLogin({ email: userEmail });
          return true;
        }
      }
    } catch (err) {
      console.log('checkAuth: Error:', err);
      // Not logged in, do nothing
    }
    console.log('checkAuth: Auth check failed');
    return false;
  };

  useEffect(() => {
    // Check on mount
    checkAuth();

    // Listen for messages from the OAuth popup
    const handleMessage = async (event: MessageEvent) => {
      console.log('Received message:', event.data, 'from:', event.origin);
      // Verify origin for security
      if (event.origin !== window.location.origin) {
        console.log('Origin mismatch, ignoring');
        return;
      }
      
      // Handle both our callback message and backend's auth message
      if (event.data?.type === 'oauth-success' || event.data?.type === 'amd-auth-success') {
        console.log('OAuth success message received');
        setMsBusy(false);
        
        // If backend provided user data directly, use it
        if (event.data.user?.email) {
          console.log('Using user data from message:', event.data.user.email);
          onLogin({ email: event.data.user.email });
        } else {
          // Otherwise check auth status
          console.log('Checking auth...');
          await checkAuth();
        }
      } else if (event.data?.type === 'oauth-error') {
        console.log('OAuth error message received:', event.data.error);
        setMsBusy(false);
        const errorMsg = event.data.error === 'invalid_state' 
          ? 'Session expired. Please try again.' 
          : event.data.error || 'Authentication failed';
        setError(errorMsg);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onLogin]);

  const submit: React.FormEventHandler = (e) => {
    e.preventDefault();
    setError('Password login is not supported. Use Microsoft login.');
  };

  const handleMsLogin = async () => {
    setError(null);
    setMsBusy(true);
    try {
      // Open OAuth on same domain (Traefik will proxy to backend)
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      // Have backend redirect back to our callback
      const callbackUrl = `${window.location.origin}/auth-callback.html`;
      
      const popup = window.open(
        `/auth/login?redirect=${encodeURIComponent(callbackUrl)}`,
        'oauth-popup',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
      );

      if (!popup) {
        setError('Popup was blocked. Please allow popups for this site.');
        setMsBusy(false);
        return;
      }

      // Just wait for the postMessage from the callback page
      // The message handler in useEffect will handle the oauth-success/oauth-error events
    } catch (e) {
      setError('Unable to initiate Microsoft login.');
      setMsBusy(false);
    }
  };

  return (
    <div className="login-shell">
      <form className="login-card" onSubmit={submit} aria-label="Login form">
        <h1>S3 Subcontractor Portal</h1>
        <button type="button" className="ms-login-btn" onClick={handleMsLogin} disabled={msBusy}>
          {msBusy ? 'Redirecting to Microsoft login…' : 'Sign in with Microsoft'}
        </button>
        <div className="divider"><span>OR</span></div>
        <div className="field">
          <label htmlFor="login-email">Email</label>
          <input id="login-email" type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required />
        </div>
        <div className="field">
          <label htmlFor="login-password">Password</label>
          <div className="pw-wrap">
            <input id="login-password" type={showPw ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required minLength={4} />
            <button type="button" className="ghost small toggle-pw" onClick={() => setShowPw(s => !s)} aria-label={showPw ? 'Hide password' : 'Show password'}>{showPw ? 'Hide' : 'Show'}</button>
          </div>
        </div>
        {error && <div className="form-error" role="alert">{error}</div>}
        <button type="submit" className="primary" disabled>{'Sign In'}</button>
        <div className="fine-print">Password login is disabled. Use Microsoft login above.</div>
      </form>
    </div>
  );
};
